import { useEffect, useState } from 'react';
import { MutateOptions } from '@tanstack/react-query';
import { useSessionStorage } from 'usehooks-ts';
import { MonitoringType } from 'shared/components/monitoring/monitoring-type';
import useTableFiltering, { TableFiltering } from 'shared/components/table/hooks/table-filtering';
import FilterSessionStorageKeys from 'shared/constants/filterSessionStorageKeys';
import SelectedOrganisationTreeNode from 'shared/features/organisation/models/selected-organisation-tree-node';

import { Connector } from 'features/connectors/domain/connector';
import ConnectorFilter, { ConnectorFilterValueType } from 'features/connectors/domain/connector-filter';
import ConnectorMonitoringFilter from 'features/connectors/domain/connector-monitoring-filter';
import { useGetMonitorFilters } from 'features/connectors/domain/queries/connector-queries';
import { ReadConnectorsQuery } from 'features/connectors/domain/read-connectors-query';
import { ReadConnectorsResponse } from 'features/connectors/domain/read-connectors-response';

export interface ConnectorFiltersHook {
  activeFilters: ConnectorFilter[];
  searchBarValue: string;
  selectedOrganisationUnits: SelectedOrganisationTreeNode[];
  changeSelectedOrganisationUnits: (selectedOrganisationUnits: SelectedOrganisationTreeNode[]) => void;

  submitSearch: (query: string) => void;
  selectOption: (filterValueType: ConnectorFilterValueType, keys: string[]) => void;
  clearFilters: () => void;

  monitorFilters: ConnectorMonitoringFilter[];
  monitorFiltersIsSuccess: boolean;
  getMonitorFilter: (monitoringType: MonitoringType) => ConnectorMonitoringFilter;
  selectMonitorFilter: (monitoringType: MonitoringType, selected: boolean) => void;
  isActiveMonitorFilter: (monitoringType: MonitoringType) => boolean;
  loadMonitorFilters: () => void;

  readConnectorsQuery: ReadConnectorsQuery;

  table: TableFiltering<Connector>;
}

const useConnectorFilters = (
  riggerReadConnectors: (
    variables: ReadConnectorsQuery,
    options?: MutateOptions<ReadConnectorsResponse, Error, ReadConnectorsQuery, unknown> | undefined,
  ) => void,
): ConnectorFiltersHook => {
  const [activeFilters, setActiveFilters] = useState<ConnectorFilter[]>([]);
  const table = useTableFiltering<Connector>({
    defaultSort: {
      isAscending: true,
      property: 'name',
    },
    sessionStorageKey: 'connectors-sort',
  });

  const defaultReadConnectorsQuery: ReadConnectorsQuery = {
    pagination: table.currentPagination[0],
    sort: table.sortFromSessionStorage ?? { isAscending: true, property: 'name' },
    filters: [],
    searchQuery: '',
  };

  const [readConnectorsQuery, setReadConnectorsQuery] = useState<ReadConnectorsQuery>(defaultReadConnectorsQuery);

  const { data: monitorFiltersData, isSuccess: monitorFiltersIsSuccess, refetch: refetchMonitorFilters } = useGetMonitorFilters();

  const [connectorFiltersFromSessionStorage, setConnectorFiltersFromSessionStorage] = useSessionStorage<ConnectorFilter[]>(
    FilterSessionStorageKeys.CONNECTOR_FILTERS,
    [],
  );
  const [selectedOrganisationUnitsFromSessionStorage, setSelectedOrganisationUnitsFromSessionStorage] = useSessionStorage<
    SelectedOrganisationTreeNode[]
  >(FilterSessionStorageKeys.CONNECTOR_ORG_UNITS, []);
  const [searchBarValueFromSessionStorage, setSearchBarFromSessionStorage] = useSessionStorage<string>(
    FilterSessionStorageKeys.CONNECTOR_SEARCH_BAR,
    '',
  );

  useEffect(() => {
    buildAndSaveQuery();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    table.currentPage,
    table.currentRowsPerPage,
    table.sortFromSessionStorage,
    connectorFiltersFromSessionStorage,
    searchBarValueFromSessionStorage,
  ]);

  const submitSearch = (query: string) => {
    setSearchBarFromSessionStorage(query);
    table.resetPaging();
  };

  const selectOption = (filterValueType: ConnectorFilterValueType, keys: string[]) => {
    let updatedFiltersList = [...readConnectorsQuery.filters];
    const filterIndex = updatedFiltersList.findIndex((x) => x.filterValueType === filterValueType);

    if (filterIndex === -1) {
      updatedFiltersList.push({
        filterValueType: filterValueType,
        values: keys,
      });
    } else {
      updatedFiltersList[filterIndex] = {
        filterValueType: filterValueType,
        values: keys,
      };
    }

    updatedFiltersList = updatedFiltersList.filter((x) => x.values.length > 0);

    setConnectorFiltersFromSessionStorage(updatedFiltersList);
    table.resetPaging();
  };

  const changeSelectedOrganisationUnits = (selectedOrganisationUnits: SelectedOrganisationTreeNode[]) =>
    setSelectedOrganisationUnitsFromSessionStorage(selectedOrganisationUnits);

  const clearFilters = () => {
    setActiveFilters([]);
    setConnectorFiltersFromSessionStorage([]);
    setSelectedOrganisationUnitsFromSessionStorage([]);
    setSearchBarFromSessionStorage('');
    setReadConnectorsQuery(defaultReadConnectorsQuery);

    table.resetPaging();
  };

  const getMonitorFilter = (monitoringType: MonitoringType) => {
    const filter = monitorFiltersData?.find((filter) => filter.monitoringFilterType === monitoringType);
    if (filter) {
      return filter;
    }

    return {
      monitoringFilterType: MonitoringType.All,
      count: monitorFiltersData?.reduce((total, filter) => total + filter.count, 0),
    } as ConnectorMonitoringFilter;
  };

  const isActiveMonitorFilter = (monitoringType: MonitoringType) => {
    const filter = activeFilters.find((x) => x.filterValueType === ConnectorFilterValueType.Monitor);
    if (!filter) {
      return false;
    }

    return filter.values.includes(monitoringType);
  };

  const loadMonitorFilters = () => {
    refetchMonitorFilters();
  };

  const selectMonitorFilter = (monitoringType: MonitoringType, selected: boolean) => {
    let activeMonitorFilters = [...(activeFilters.find((x) => x.filterValueType === ConnectorFilterValueType.Monitor)?.values ?? [])];

    if (selected && !activeMonitorFilters.includes(monitoringType)) {
      activeMonitorFilters = [...activeMonitorFilters, monitoringType];
    } else if (!selected && activeMonitorFilters.includes(monitoringType)) {
      const activeMonitorFilterIndex = activeMonitorFilters.indexOf(monitoringType);
      if (activeMonitorFilterIndex >= 0) {
        activeMonitorFilters.splice(activeMonitorFilterIndex, 1);
      }
    }

    selectOption(ConnectorFilterValueType.Monitor, activeMonitorFilters);
    loadMonitorFilters();
  };

  const buildAndSaveQuery = () => {
    if (
      table.currentPagination[0].skip !== readConnectorsQuery.pagination.skip ||
      table.currentPagination[0].take !== readConnectorsQuery.pagination.take ||
      table.sortFromSessionStorage !== readConnectorsQuery.sort ||
      connectorFiltersFromSessionStorage !== readConnectorsQuery.filters ||
      searchBarValueFromSessionStorage !== readConnectorsQuery.searchQuery
    ) {
      setActiveFilters(connectorFiltersFromSessionStorage);
      const newQuery = {
        pagination: table.currentPagination[0],
        sort: table.sortFromSessionStorage,
        filters: connectorFiltersFromSessionStorage,
        searchQuery: searchBarValueFromSessionStorage,
      };
      setReadConnectorsQuery(newQuery);
      riggerReadConnectors(newQuery);
    }
  };

  return {
    activeFilters,
    searchBarValue: searchBarValueFromSessionStorage,
    selectedOrganisationUnits: selectedOrganisationUnitsFromSessionStorage,
    changeSelectedOrganisationUnits,

    readConnectorsQuery,

    selectOption,
    submitSearch,
    clearFilters,

    monitorFiltersIsSuccess,
    monitorFilters: monitorFiltersData ?? [],
    getMonitorFilter,
    loadMonitorFilters,
    selectMonitorFilter,
    isActiveMonitorFilter,

    table,
  };
};

export default useConnectorFilters;
